// components/Email.js
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  Send,
  CheckCircle,
  NotificationsActive,
  TrendingUp,
  People,
  Videocam,
  RocketLaunch,
  Verified,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { postData } from "../services/Request";
import SP from "../assets/HomePage/Megaphone.png";
import "../styles/Email.css";

const Email = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [email, setEmail] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isDisable, setIsDisable] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const stats = [
    { icon: <People />, label: "Subscribers", value: "10,000+" },
    { icon: <Videocam />, label: "Webinars", value: "50+" },
    { icon: <TrendingUp />, label: "Growth Rate", value: "25%" },
  ];

  const handleEmailChange = (e) => {
    const enteredEmail = e.target.value;
    setEmail(enteredEmail);
    validateEmail(enteredEmail);
  };

  const validateEmail = (inputEmail) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(inputEmail));
  };

  const saveEmail = () => {
    setIsDisable(true);

    if (isValidEmail && email !== "") {
      postData("email", { email: email })
        .then((response) => {
          if (response.status === true) {
            Swal.fire({
              title: "🎉 Subscription Successful!",
              text:
                response.message ||
                "You'll now receive updates on new videos and webinars.",
              icon: "success",
              confirmButtonColor: "#2BAD65",
              background: "#ffffff",
              color: "#1a1a1a",
              confirmButtonText: "Awesome!",
              iconColor: "#2BAD65",
            });
            setEmail("");
            setIsDisable(false);
          } else {
            Swal.fire({
              title: "Oops...",
              text:
                response.message || "Something went wrong. Please try again.",
              icon: "info",
              confirmButtonColor: "#2BAD65",
              background: "#ffffff",
              color: "#1a1a1a",
            });
            setIsDisable(false);
          }
        })
        .catch((error) => {
          Swal.fire({
            title: "Error",
            text: "Network error. Please check your connection.",
            icon: "error",
            confirmButtonColor: "#2BAD65",
            background: "#ffffff",
            color: "#1a1a1a",
          });
          setIsDisable(false);
        });
    } else {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address.",
        icon: "warning",
        confirmButtonColor: "#2BAD65",
        background: "#ffffff",
        color: "#1a1a1a",
      });
      setIsDisable(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      saveEmail();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      className="email-section"
      sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 3 },
        background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(43, 173, 101, 0.05) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(160, 239, 72, 0.04) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite 2s",
        }}
      />

      {/* Floating Particles */}
      {[...Array(4)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${15 + i * 25}%`,
            left: `${5 + (i % 3) * 30}%`,
            width: `${4 + i * 2}px`,
            height: `${4 + i * 2}px`,
            borderRadius: "50%",
            backgroundColor:
              i % 2 === 0
                ? "rgba(43, 173, 101, 0.12)"
                : "rgba(160, 239, 72, 0.12)",
            animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
            zIndex: 0,
          }}
        />
      ))}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Fade in timeout={800}>
          <Box>
            {/* Section Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Chip
                icon={<NotificationsActive sx={{ fontSize: 16 }} />}
                label="📬 Stay Updated"
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.1)",
                  color: "#2BAD65",
                  fontWeight: 600,
                  mb: 2,
                  px: 2,
                  border: "1px solid rgba(43, 173, 101, 0.15)",
                  "& .MuiChip-icon": {
                    color: "#2BAD65",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(43, 173, 101, 0.15)",
                  },
                  transition: "all 0.3s ease",
                }}
              />

              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3.2rem" },
                  color: "#1a1a1a",
                  mb: 1.5,
                }}
              >
                Be Notified of{" "}
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                    backgroundSize: "200% 200%",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: "gradientMove 4s ease-in-out infinite",
                  }}
                >
                  New Videos & Webinars
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "rgba(26, 26, 26, 0.6)",
                  maxWidth: 550,
                  mx: "auto",
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  lineHeight: 1.7,
                }}
              >
                Become part of a broad community to get in on grooming tips,
                education videos, notifications, and anything that'll help you
                grow.
              </Typography>
            </Box>

            {/* Stats Row */}
            <Stack
              direction="row"
              spacing={{ xs: 3, md: 5 }}
              justifyContent="center"
              sx={{ mb: 4, flexWrap: "wrap", gap: 2 }}
            >
              {stats.map((stat, index) => (
                <Slide
                  key={index}
                  direction="up"
                  in
                  timeout={600 + index * 200}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(43, 173, 101, 0.08)",
                          borderRadius: "50%",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#2BAD65",
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#1a1a1a",
                          fontSize: { xs: "1.2rem", md: "1.5rem" },
                          background:
                            "linear-gradient(135deg, #2BAD65, #A0EF48)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(26, 26, 26, 0.3)",
                        fontSize: { xs: "0.65rem", md: "0.75rem" },
                        letterSpacing: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Slide>
              ))}
            </Stack>

            {/* Main Subscription Card */}
            <Slide direction="up" in timeout={800}>
              <Paper
                elevation={0}
                className="email-card"
                sx={{
                  maxWidth: 700,
                  mx: "auto",
                  p: { xs: 3, sm: 4, md: 4.5 },
                  background: "rgba(255, 255, 255, 0.85)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(43, 173, 101, 0.08)",
                  borderRadius: 4,
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.06)",
                    borderColor: "rgba(43, 173, 101, 0.15)",
                  },
                }}
              >
                {/* Animated Gradient Border */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background:
                      "linear-gradient(90deg, #2BAD65, #A0EF48, #2BAD65)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 3s linear infinite",
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {/* Megaphone Icon with Animation */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: { xs: 70, sm: 90 },
                      height: { xs: 70, sm: 90 },
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, rgba(43, 173, 101, 0.1), rgba(160, 239, 72, 0.05))",
                      border: "2px solid rgba(43, 173, 101, 0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: "float 3s ease-in-out infinite",
                      position: "relative",
                    }}
                  >
                    <img
                      src={SP}
                      alt="Subscribe"
                      style={{
                        width: 45,
                        height: 45,
                        objectFit: "contain",
                        filter:
                          "drop-shadow(0 4px 20px rgba(43, 173, 101, 0.15))",
                      }}
                    />
                    {/* Notification Dot */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#FF6B6B",
                        border: "2px solid #F7F8F2",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, width: "100%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(26, 26, 26, 0.4)",
                        mb: 1,
                        fontSize: "0.85rem",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Subscribe to our newsletter
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1,
                      }}
                    >
                      <TextField
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={handleEmailChange}
                        onKeyPress={handleKeyPress}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        error={!isValidEmail && email !== ""}
                        helperText={
                          !isValidEmail && email !== ""
                            ? "Please enter a valid email address"
                            : ""
                        }
                        disabled={isDisable}
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: isFocused
                                ? "#2BAD65"
                                : "rgba(43, 173, 101, 0.12)",
                              borderWidth: isFocused ? 2 : 1,
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.2)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: "#1a1a1a",
                            py: 1.5,
                            "&::placeholder": {
                              color: "rgba(26, 26, 26, 0.3)",
                              opacity: 1,
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                            fontSize: "0.7rem",
                          },
                        }}
                      />

                      <Button
                        variant="contained"
                        onClick={saveEmail}
                        disabled={isDisable || !isValidEmail || email === ""}
                        startIcon={!isDisable ? <Send /> : null}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontSize: "1rem",
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #2BAD65, #A0EF48)",
                          borderRadius: 2,
                          minWidth: { xs: "100%", sm: 160 },
                          color: "#ffffff",
                          boxShadow: "0 4px 25px rgba(43, 173, 101, 0.3)",
                          "&:hover": {
                            transform: "translateY(-2px) scale(1.02)",
                            boxShadow: "0 8px 35px rgba(43, 173, 101, 0.4)",
                            background:
                              "linear-gradient(135deg, #239A56, #8EDB40)",
                          },
                          "&:disabled": {
                            backgroundColor: "rgba(43, 173, 101, 0.15)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {isDisable ? (
                          <>
                            <CircularProgress
                              size={20}
                              sx={{ color: "#ffffff" }}
                            />
                            Subscribing...
                          </>
                        ) : (
                          "Subscribe"
                        )}
                      </Button>
                    </Box>

                    {/* Trust Badges */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CheckCircle sx={{ fontSize: 14, color: "#2BAD65" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(26, 26, 26, 0.3)",
                            fontSize: "0.7rem",
                          }}
                        >
                          No spam
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CheckCircle sx={{ fontSize: 14, color: "#2BAD65" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(26, 26, 26, 0.3)",
                            fontSize: "0.7rem",
                          }}
                        >
                          Unsubscribe anytime
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CheckCircle sx={{ fontSize: 14, color: "#2BAD65" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(26, 26, 26, 0.3)",
                            fontSize: "0.7rem",
                          }}
                        >
                          Secure & encrypted
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Paper>
            </Slide>

            {/* Social Proof */}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 4, flexWrap: "wrap", gap: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(26, 26, 26, 0.2)" }}
              >
                Trusted by 1000+ learners worldwide
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Box
                    key={star}
                    component="span"
                    sx={{
                      color: "#FFD93D",
                      fontSize: 14,
                    }}
                  >
                    ★
                  </Box>
                ))}
              </Box>
              <Verified sx={{ color: "#2BAD65", fontSize: 14 }} />
            </Stack>
          </Box>
        </Fade>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            backgroundColor: "#ffffff",
            color: "#1a1a1a",
            border: "1px solid rgba(43, 173, 101, 0.08)",
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#2BAD65" : "#FF6B6B",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add keyframe animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }

          @keyframes gradientMove {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}
      </style>
    </Box>
  );
};

export default Email;
